import { Box, withStyles, MaterialSymbol } from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFetchBankHolidays from '@/hooks/useApi/useFetchBankHolidays';
import { parse, format } from 'date-fns';
import { useDataFetchIndicator } from '@/hooks';

import TabButton from '../../components/TabButton';
import Table from '../../components/Table';

import styles from './BankHolidays.module.scss';

import { Title } from 'UI';

const cn = withStyles(styles);

type FC = FunctionComponent;

type Tab = 'yearly' | 'monthly';

type Holiday = {
  Country: string;
  HolidayDate: string;
  HolidayName: string;
};

type TableColumn = {
  columnKey: string;
  title: string;
  align?: 'left' | 'center' | 'right';
};

type BankHolidays = Holiday[];

const BankHolidays: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'HomepageRevamp.Widgets.BankHolidays' });
  const { fetchBankHolidays } = useFetchBankHolidays();
  const [activeTab, setActiveTab] = useState<Tab>('yearly');
  const [holidays, setHolidays] = useState<BankHolidays>([]);
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const now = new Date();
  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  useEffect(() => {
    const getHolidays = async () => {
      try {
        setDataFetchingInfo(true);
        const res = await fetchBankHolidays();
        if (res.Success) {
          setHolidays(res.BankHolidays);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setDataFetchingInfo(false);
      }
    };

    getHolidays();
  }, []);

  const validHolidays = useMemo(() => {
    return holidays
      .filter((data) => new Date(data.HolidayDate).getUTCFullYear() === currentYear)
      .sort((a, b) => new Date(a.HolidayDate).getTime() - new Date(b.HolidayDate).getTime());
  }, [holidays, currentYear]);

  const holidaysToRender = useMemo(() => {
    return activeTab === 'monthly'
      ? validHolidays.filter((data) => new Date(data.HolidayDate).getUTCMonth() === currentMonth)
      : validHolidays;
  }, [validHolidays, activeTab, currentMonth]);

  const options: { label: string; value: Tab }[] = [
    { label: t('yearly'), value: 'yearly' },
    { label: t('monthly'), value: 'monthly' },
  ];

  const columns: TableColumn[] = [
    { columnKey: 'bank-holiday', title: t('holiday') },
    { columnKey: 'bank-affected-rails', title: t('affectedRails') },
    { columnKey: 'bank-date', title: t('date'), align: 'right' },
  ];

  const mergeHolidays = (list: Holiday[]) => {
    const map = new Map<string, { name: string; date: string; rails: ('EFT' | 'ACH')[] }>();

    list.forEach(({ Country, HolidayName, HolidayDate }) => {
      const rail = Country === 'Canada' ? 'EFT' : 'ACH';
      const existing = map.get(HolidayName);
      map.set(HolidayName, {
        name: HolidayName,
        date: HolidayDate,
        rails: existing ? [...existing.rails, rail] : [rail],
      });
    });

    return Array.from(map.values());
  };

  const rows = useMemo(() => {
    return mergeHolidays(holidaysToRender).map((data) => ({
      rowKey: [data.name, data.rails, data.date].join('-'),
      [t('holiday')]: <span style={{ fontWeight: 600, color: 'rgba(45,45,45,0.75)' }}>{data.name}</span>,
      [t('affectedRails')]: data.rails.join(', '),
      [t('date')]: format(parse(data.date, 'yyyy-MM-dd', new Date()), 'MMM d, yyyy'),
    }));
  }, [holidaysToRender, t]);

  const NoHoliday = () => {
    return (
      <div style={{ flex: 1 }}>
        <div className={cn('noHolidayContainer')}>
          <MaterialSymbol icon="calendar_today" size="72px" fillAmount={1} color="rgba(45, 45, 45, 0.75)" />
          <Title level="5">{t('noHolidayMessage', { date: format(new Date(), 'MMMM yyyy') })}</Title>
        </div>
      </div>
    );
  };

  return (
    <>
      <Box ai="center" jc="sb" mb="md" height="auto">
        <Title level="5">{t('title')}</Title>
        <TabButton
          options={options}
          value={activeTab}
          onSelect={(val: Tab) => {
            setActiveTab(val);
          }}
        />
      </Box>
      {rows.length === 0 && activeTab === 'monthly' ? (
        <NoHoliday />
      ) : (
        <Table id="holiday" columns={columns} rows={rows} bodyHeight="calc(100% - 50px)" cellWidth="110px" />
      )}
    </>
  );
};

export default BankHolidays;
